import { useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { CheckCircleIcon, XCircleIcon } from "@phosphor-icons/react";

import { IMPORT_OPTION_ID } from "../../../screens/Vacations/components/Balances/constants/importOptions";

export type ImportProgressStatus = "loading" | "success" | "error";

export type ImportProgressTexts = {
  loadingHeader: string;
  loadingMessage: string;
  successHeader: string;
  successMessage: string;
  errorHeader: string;
  errorMessage: string;
};

type ImportProgressModalPropsT = {
  selectedOption: (typeof IMPORT_OPTION_ID)[keyof typeof IMPORT_OPTION_ID];
  status: ImportProgressStatus;
  texts: ImportProgressTexts;
  errorMessage?: string;
  errorList?: { rowNumber: number; collaborator: string; error: string }[];
  onClose: () => void;
};

const AUTO_CLOSE_DELAY_MS = 3000;

const ImportProgressModal = ({
  selectedOption,
  status,
  texts,
  errorMessage,
  errorList,
  onClose,
}: ImportProgressModalPropsT) => {
  const isLoading = status === "loading";
  const isSuccess = status === "success";

  const hasErrors =
    status === "error" && selectedOption === IMPORT_OPTION_ID.TEMPLATE;

  useEffect(() => {
    if (isLoading || hasErrors) return;
    const timer = setTimeout(onClose, AUTO_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isLoading, hasErrors, onClose]);

  return (
    <Dialog
      visible
      header={false}
      closable={!isLoading}
      onHide={isLoading ? () => {} : onClose}
      style={{ width: "90vw", maxWidth: "50rem" }}
      footer={false}
      aria-labelledby="import-modal-title"
      aria-modal
    >
      <div
        className="flex flex-col items-center gap-3 py-3 text-center"
        aria-live="polite"
        aria-atomic="true"
        aria-busy={isLoading}
      >
        {isLoading && (
          <>
            <div
              className="animate-spin rounded-full h-16 w-16 border-4 border-secondary-PCS001 border-t-transparent"
              aria-hidden="true"
            />
            <p
              id="import-modal-title"
              className="text-base font-semibold text-blue-600"
            >
              {texts.loadingHeader}
            </p>
            <p className="text-sm text-secondaryText">{texts.loadingMessage}</p>
          </>
        )}
        {isSuccess && (
          <>
            <CheckCircleIcon
              size={64}
              weight="fill"
              className="text-green-500"
              aria-hidden
            />
            <p
              id="import-modal-title"
              className="text-base font-semibold text-blue-600"
            >
              {texts.successHeader}
            </p>
            <p className="text-sm text-secondaryText">{texts.successMessage}</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircleIcon
              size={64}
              weight="fill"
              className="text-red-500"
              aria-hidden
            />
            <p
              id="import-modal-title"
              className="text-base font-semibold text-blue-600"
            >
              {texts.errorHeader}
            </p>
            <p className="text-sm text-secondaryText">
              {errorMessage ?? texts.errorMessage}
            </p>
            {errorList && errorList.length > 0 && (
              <div className="w-full overflow-auto max-h-48 text-left mt-2">
                <table
                  className="w-full text-xs border-collapse"
                  aria-label={texts.errorHeader}
                >
                  <thead>
                    <tr>
                      <th scope="col" className="border px-2 py-1">
                        Reg.
                      </th>
                      <th scope="col" className="border px-2 py-1">
                        Colaborador
                      </th>
                      <th scope="col" className="border px-2 py-1">
                        Error
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorList.map((item) => (
                      <tr key={item.rowNumber}>
                        <td className="border px-2 py-1">{item.rowNumber}</td>
                        <td className="border px-2 py-1">
                          {item.collaborator}
                        </td>
                        <td className="border px-2 py-1">{item.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};

export default ImportProgressModal;
